import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAllProducts, getBranchInventory, createServiceOrder, createCustomer, getRecentCustomers, formatPhone, getCompanyConfig, getTopSoldProducts } from '../lib/api';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money    = (n) => Number(n || 0).toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });

const isService = (product) => {
  const type = String(product?.product_type || product?.type || '').trim().toUpperCase();
  return (
    type === 'SERVICIO' ||
    type === 'SERVICE'  ||
    product?.track_inventory === false ||
    product?.track_inventory === 'false' ||
    product?.track_inventory === 0
  );
};

const defaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

const SO_STATUS_COLORS = {
  RECIBIDO:   'bg-blue-100 text-blue-700',
  EN_PROCESO: 'bg-yellow-100 text-yellow-700',
  LISTO:      'bg-green-100 text-green-700',
};

const emptySubOrderForm = {
  item_type: 'catalog',
  product_id: '', product_name: '',
  variant_size: '', variant_color: '',
  item_description: '',
  item_price: '',
  services: [],
  work_notes: '',
};

const ServiceOrderNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [products, setProducts]               = useState([]);
  const [branchInventory, setBranchInventory] = useState([]);
  const [saving, setSaving]                   = useState(false);
  const [cardSurchargePct, setCardSurchargePct] = useState(0);
  const [paymentMethod, setPaymentMethod]       = useState('efectivo');
  const [topProducts, setTopProducts]           = useState([]);
  const [topServices, setTopServices]           = useState([]);
  const [productSearch, setProductSearch]       = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [serviceSearch, setServiceSearch]         = useState('');
  const [showServiceSearch, setShowServiceSearch] = useState(false);
  const [showPriceNote, setShowPriceNote]         = useState(() => sessionStorage.getItem('priceNoteDismissed') !== '1');

  // ── Customer ─────────────────────────────────────────────────────────
  const [recentCustomers, setRecentCustomers]           = useState([]);
  const [customerSearch, setCustomerSearch]             = useState('');
  const [customerResults, setCustomerResults]           = useState([]);
  const [customerSearching, setCustomerSearching]       = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerInputFocused, setCustomerInputFocused] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm]   = useState(false);
  const [newCustomerData, setNewCustomerData]           = useState({ name: '', last_name: '', phone: '', email: '' });
  const [phoneWarning, setPhoneWarning]                 = useState(null);

  // ── Main form ─────────────────────────────────────────────────────────
  const [form, setForm] = useState({ customer_id: '', work_notes: '', due_date: defaultDueDate(), initial_payment: '' });
  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Sub-orders list ───────────────────────────────────────────────────
  const [subOrders, setSubOrders]           = useState([]);
  const [showSubOrderForm, setShowSubOrderForm] = useState(false);
  const [editingIdx, setEditingIdx]         = useState(null); // null = new, number = editing

  // ── Sub-order form ────────────────────────────────────────────────────
  const [soForm, setSoForm]     = useState({ ...emptySubOrderForm });
  const setSoField = (k, v)     => setSoForm(p => ({ ...p, [k]: v }));

  // ── Derived product lists ─────────────────────────────────────────────
  const baseProducts    = useMemo(() => products.filter(p => !isService(p)), [products]);
  const serviceProducts = useMemo(() => products.filter(p => isService(p)),  [products]);
  const totalAmount     = useMemo(() => subOrders.reduce((s, so) => s + so.subtotal, 0), [subOrders]);
  const cardSurchargeAmt = useMemo(() => {
    if (paymentMethod !== 'tarjeta' || cardSurchargePct <= 0) return 0;
    return Math.round(totalAmount * cardSurchargePct / 100 * 100) / 100;
  }, [totalAmount, paymentMethod, cardSurchargePct]);
  const totalWithSurcharge = useMemo(() => totalAmount + cardSurchargeAmt, [totalAmount, cardSurchargeAmt]);
  const CARD_FACTOR      = cardSurchargePct > 0 ? (1 + cardSurchargePct / 100) : 1;
  const cardPrice        = (p) => cardSurchargePct > 0 ? Math.round(toNumber(p.price) * CARD_FACTOR * 100) / 100 : null;
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    const q = productSearch.toLowerCase();
    return baseProducts.filter(p => p.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [baseProducts, productSearch]);
  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return [];
    const q = serviceSearch.toLowerCase();
    return serviceProducts.filter(p => p.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [serviceProducts, serviceSearch]);

  // ── Catalog variants for sub-order form ──────────────────────────────
  const soCatalogProduct = useMemo(
    () => baseProducts.find(p => p.id === soForm.product_id) || null,
    [baseProducts, soForm.product_id]
  );

  const soCatalogVariants = useMemo(() => {
    if (!soForm.product_id) return [];
    const fromBranch = branchInventory.filter(i => i.product_id === soForm.product_id);
    if (fromBranch.length > 0) return fromBranch;
    if (!soCatalogProduct) return [];
    const sizes  = soCatalogProduct.sizes?.length  > 0 ? soCatalogProduct.sizes  : [''];
    const colors = soCatalogProduct.colors?.length > 0 ? soCatalogProduct.colors : [''];
    const grid   = [];
    for (const s of sizes) for (const c of colors) grid.push({ size: s, color: c, stock: null });
    return grid;
  }, [branchInventory, soForm.product_id, soCatalogProduct]);

  const soAvailableSizes = useMemo(
    () => [...new Set(soCatalogVariants.map(v => v.size || '').filter(Boolean))],
    [soCatalogVariants]
  );

  const soAvailableColors = useMemo(() => {
    const filtered = soForm.variant_size
      ? soCatalogVariants.filter(v => (v.size || '') === soForm.variant_size)
      : soCatalogVariants;
    return [...new Set(filtered.map(v => v.color || '').filter(Boolean))];
  }, [soCatalogVariants, soForm.variant_size]);

  const soVariantStock = useMemo(() => {
    if (!soForm.product_id) return null;
    const match = soCatalogVariants.find(
      v => (v.size  || '') === (soForm.variant_size  || '') &&
           (v.color || '') === (soForm.variant_color || '')
    );
    return match?.stock ?? null;
  }, [soCatalogVariants, soForm.product_id, soForm.variant_size, soForm.variant_color]);

  const soItemPrice = useMemo(() => {
    if (soForm.item_type === 'catalog') return toNumber(soCatalogProduct?.price || 0);
    return toNumber(soForm.item_price || 0);
  }, [soForm.item_type, soCatalogProduct, soForm.item_price]);

  const soServicesTotal = useMemo(
    () => soForm.services.reduce((s, sv) => s + sv.subtotal, 0),
    [soForm.services]
  );

  const soSubtotal = useMemo(() => soItemPrice + soServicesTotal, [soItemPrice, soServicesTotal]);

  // ── Effects ───────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const prods = await getAllProducts();
        setProducts(Array.isArray(prods) ? prods : (prods?.products || []));
      } catch (e) { toast.error(e?.response?.data?.error || 'Error cargando productos'); }
    })();
    getRecentCustomers().then(data => setRecentCustomers(Array.isArray(data) ? data : [])).catch(() => {});
    getCompanyConfig().then(cfg => setCardSurchargePct(parseFloat(cfg?.card_surcharge_pct || 0))).catch(() => {});
    getTopSoldProducts(5, user?.branch_id || '', 'products').then(setTopProducts).catch(() => {});
    getTopSoldProducts(3, user?.branch_id || '', 'services').then(setTopServices).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    const branchId = user?.branch_id;
    if (!branchId) return;
    getBranchInventory(branchId)
      .then(data => setBranchInventory(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [user]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => searchCustomers(customerSearch), 350);
    return () => clearTimeout(t);
  }, [customerSearch]); // eslint-disable-line

  // ── Customer handlers ─────────────────────────────────────────────────
  const searchCustomers = async (q) => {
    if (!q || q.length < 2) { setCustomerResults([]); return; }
    setCustomerSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res   = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/customers?q=${encodeURIComponent(q)}&per_page=8`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setCustomerResults(data.customers || data || []);
    } catch { setCustomerResults([]); }
    finally   { setCustomerSearching(false); }
  };

  const [selectedCustomerData, setSelectedCustomerData] = React.useState(null);

  const selectCustomer = (c) => {
    setField('customer_id', c.id);
    setCustomerSearch(`${c.name} ${c.last_name}`);
    setShowCustomerDropdown(false);
    setCustomerResults([]);
    setSelectedCustomerData(c);
  };

  const handleCreateCustomer = async (confirmDuplicate = false) => {
    if (!newCustomerData.name || !newCustomerData.phone) {
      toast.error('Nombre y teléfono son requeridos'); return;
    }
    if (!isValidPhoneNumber(newCustomerData.phone)) {
      toast.error('Número de teléfono inválido'); return;
    }
    try {
      const payload = confirmDuplicate ? { ...newCustomerData, confirm_duplicate_phone: true } : newCustomerData;
      const created = await createCustomer(payload);
      selectCustomer(created);
      setShowNewCustomerForm(false);
      setPhoneWarning(null);
      setNewCustomerData({ name: '', last_name: '', phone: '', email: '' });
      toast.success('Cliente creado');
      getRecentCustomers().then(data => setRecentCustomers(Array.isArray(data) ? data : [])).catch(() => {});
    } catch (e) {
      const res = e?.response?.data;
      if (e?.response?.status === 409 && res?.warning === 'duplicate_phone') {
        setPhoneWarning(res.message); return;
      }
      toast.error(res?.error || 'Error creando cliente');
    }
  };

  // ── Sub-order form handlers ───────────────────────────────────────────
  const handleSoCatalogProductChange = (productId) => {
    const prod = baseProducts.find(p => p.id === productId);
    setSoForm(p => ({
      ...p,
      product_id:    productId,
      product_name:  prod?.name || '',
      variant_size:  '',
      variant_color: '',
    }));
  };

  const handleSoSizeChange = (size) => {
    setSoField('variant_size', size);
    setSoField('variant_color', '');
  };

  const addSoServiceById = (id) => {
    const svc = serviceProducts.find(p => p.id === id);
    if (!svc) return;
    const unit_price = toNumber(svc.price || 0);
    setSoForm(p => {
      const existing = p.services.find(s => s.product_id === svc.id);
      const next = existing
        ? p.services.map(s => s.product_id !== svc.id ? s : { ...s, quantity: s.quantity + 1, subtotal: round2((s.quantity + 1) * s.unit_price) })
        : [...p.services, { product_id: svc.id, name: svc.name, quantity: 1, unit_price, subtotal: unit_price }];
      return { ...p, services: next };
    });
  };


  const removeServiceFromSubOrder = (productId) =>
    setSoForm(p => ({ ...p, services: p.services.filter(s => s.product_id !== productId) }));

  const openNewSubOrderForm = () => {
    setSoForm({ ...emptySubOrderForm });
    setEditingIdx(null);
    setShowSubOrderForm(true);
  };

  const openEditSubOrderForm = (idx) => {
    const so = subOrders[idx];
    setSoForm({
      item_type:        so.item_type,
      product_id:       so.product_id    || '',
      product_name:     so.product_name  || '',
      variant_size:     so.variant_size  || '',
      variant_color:    so.variant_color || '',
      item_description: so.item_description || '',
      item_price:       so.item_type === 'external' ? String(so.item_price || '') : '',
      services:         [...so.services],
      work_notes:       so.work_notes || '',
    });
    setEditingIdx(idx);
    setShowSubOrderForm(true);
  };

  const cancelSubOrderForm = () => {
    setShowSubOrderForm(false);
    setEditingIdx(null);
    setSoForm({ ...emptySubOrderForm });
  };

  const saveSubOrder = () => {
    if (soForm.item_type === 'catalog' && !soForm.product_id)
      return toast.error('Selecciona un producto del catálogo');
    if (soForm.item_type === 'external' && !soForm.product_name.trim())
      return toast.error('Ingresa el nombre del prenda');
    if (soForm.item_type === 'catalog') {
      if (soAvailableSizes.length > 0 && !soForm.variant_size)
        return toast.error('Selecciona una talla para este producto');
      if (soAvailableColors.length > 0 && !soForm.variant_color)
        return toast.error('Selecciona un color para este producto');
    }
    if (soForm.item_type === 'catalog' && soVariantStock !== null && soVariantStock <= 0)
      return toast.error('Sin stock disponible para esta variante');
    if (soForm.services.length === 0)
      return toast.error('Agrega al menos un servicio para este prenda');
    if (!soForm.work_notes.trim())
      return toast.error('Las notas son obligatorias');
    const newSo = {
      id:               editingIdx !== null ? subOrders[editingIdx].id : String(Date.now()),
      item_type:        soForm.item_type,
      product_id:       soForm.item_type === 'catalog' ? (soForm.product_id || null) : null,
      product_name:     soForm.item_type === 'catalog' ? (soCatalogProduct?.name || soForm.product_name) : soForm.product_name.trim(),
      variant_size:     soForm.variant_size  || '',
      variant_color:    soForm.variant_color || '',
      item_description: soForm.item_description || '',
      item_price:       soItemPrice,
      services:         soForm.services,
      work_notes:       soForm.work_notes || '',
      services_total:   soServicesTotal,
      subtotal:         soSubtotal,
    };

    if (editingIdx !== null) {
      setSubOrders(p => p.map((so, i) => i === editingIdx ? newSo : so));
    } else {
      setSubOrders(p => [...p, newSo]);
    }
    cancelSubOrderForm();
  };

  const duplicateSubOrder = (idx) => {
    const so = { ...subOrders[idx], id: String(Date.now()) };
    setSubOrders(p => [...p, so]);
    toast.success('Prenda duplicada');
  };

  const removeSubOrder = (idx) => setSubOrders(p => p.filter((_, i) => i !== idx));

  // ── Submit ────────────────────────────────────────────────────────────
  const submit = async () => {
    if (!form.customer_id)       return toast.error('Selecciona un cliente');
    if (subOrders.length === 0)  return toast.error('Agrega al menos una prenda');

    const anticipo    = parseFloat(form.initial_payment) || 0;
    const isCardPay   = paymentMethod === 'tarjeta' && cardSurchargePct > 0;
    const effTotal    = isCardPay ? totalWithSurcharge : totalAmount;
    const anticiPoCash = isCardPay ? Math.round(anticipo / CARD_FACTOR * 100) / 100 : anticipo;
    if (anticipo < 0)         return toast.error('El anticipo no puede ser negativo');
    if (anticipo > effTotal)  return toast.error('El anticipo no puede superar el total');

    try {
      setSaving(true);
      const payload = {
        customer_id: form.customer_id,
        sub_orders:  subOrders.map(so => ({
          item_type:        so.item_type,
          product_id:       so.product_id   || null,
          product_name:     so.product_name,
          variant_size:     so.variant_size  || '',
          variant_color:    so.variant_color || '',
          item_description: so.item_description || '',
          item_price:       so.item_price,
          services:         so.services.map(s => ({
            product_id: s.product_id,
            quantity:   s.quantity,
            unit_price: s.unit_price,
          })),
          work_notes: so.work_notes || '',
        })),
        work_notes:      form.work_notes,
        due_date:        form.due_date,
        initial_payment: anticiPoCash,
      };
      const created = await createServiceOrder(payload);
      toast.success('Orden creada' + (anticipo > 0 ? ' — abriendo POS para cobrar el anticipo' : ''));
      if (anticipo > 0) {
        navigate(`/pos?service_order_id=${created.id}&initial_payment=${anticiPoCash.toFixed(2)}&payment_method=${paymentMethod}`);
      } else {
        navigate(`/service-orders/${created.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error creando la orden');
    } finally {
      setSaving(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────
  const round2 = (n) => Math.round(n * 100) / 100;
  const letterOf = (idx) => String.fromCharCode(65 + idx);

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto" data-testid="service-order-new-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Nueva Orden de Servicio
        </h1>
        <p className="text-slate-600">Registra las prendas y servicios a realizar.</p>
      </div>

      {/* Cliente */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Cliente</CardTitle></CardHeader>
        <CardContent>
          <div className="relative">
            <Label>Buscar cliente</Label>
            <div className="relative mt-1">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setShowCustomerDropdown(true);
                  if (!e.target.value) setField('customer_id', '');
                }}
                onFocus={() => { setCustomerInputFocused(true); customerSearch.length >= 2 && setShowCustomerDropdown(true); }}
                onBlur={() => setTimeout(() => { setShowCustomerDropdown(false); setCustomerInputFocused(false); }, 200)}
                placeholder="Buscar por nombre o teléfono..."
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 ${
                  form.customer_id ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200'
                }`}
              />
              {customerSearching && <span className="absolute right-3 top-2.5 text-xs text-slate-400">...</span>}
              {form.customer_id  && <span className="absolute right-3 top-2.5 text-cyan-500 text-xs">✓</span>}
            </div>

            {selectedCustomerData?.has_logo && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                <span>🖼️</span>
                <div>
                  <span className="font-medium">Logo disponible</span>
                  {selectedCustomerData.logo_notes && <span className="text-emerald-600 ml-2 text-xs">{selectedCustomerData.logo_notes}</span>}
                </div>
              </div>
            )}

            {showCustomerDropdown && customerResults.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                {customerResults.map(c => (
                  <button key={c.id} type="button" onMouseDown={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 hover:bg-cyan-50 text-sm border-b border-slate-100 last:border-0">
                    <span className="font-medium">{c.name} {c.last_name}</span>
                    <span className="text-slate-400 ml-2 text-xs">{formatPhone(c.phone)}</span>
                  </button>
                ))}
                <button type="button" onMouseDown={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-cyan-600 font-medium flex items-center gap-1">
                  <span>+</span> Crear nuevo cliente
                </button>
              </div>
            )}

            {showCustomerDropdown && customerSearch.length >= 2 && customerResults.length === 0 && !customerSearching && (
              <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1">
                <button type="button" onMouseDown={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-cyan-600 font-medium flex items-center gap-1">
                  <span>+</span> No encontrado — Crear cliente
                </button>
              </div>
            )}

            {showNewCustomerForm && (
              <div className="mt-2 p-3 border border-cyan-200 bg-cyan-50 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-cyan-800">Nuevo cliente</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input placeholder="Nombre *" value={newCustomerData.name}
                    onChange={e => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <input placeholder="Apellido" value={newCustomerData.last_name}
                    onChange={e => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <div className="phone-input-container">
                    <PhoneInput international defaultCountry="DO"
                      value={newCustomerData.phone}
                      onChange={v => setNewCustomerData({ ...newCustomerData, phone: v || '' })} />
                  </div>
                  <input placeholder="Email" value={newCustomerData.email}
                    onChange={e => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                </div>
                {phoneWarning && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <span className="text-base">⚠️</span>
                      <div>
                        <p className="font-semibold text-amber-900 text-xs">Teléfono duplicado</p>
                        <p className="text-xs text-amber-800">{phoneWarning}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setPhoneWarning(null)}
                        className="flex-1 text-xs border border-slate-200 rounded py-1 hover:bg-slate-50">Cancelar</button>
                      <button type="button" onClick={() => { setPhoneWarning(null); handleCreateCustomer(true); }}
                        className="flex-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded py-1 font-medium">Guardar igual</button>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button type="button" onClick={() => handleCreateCustomer(false)}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-medium py-1.5 rounded transition-colors">
                    Crear y seleccionar
                  </button>
                  <button type="button" onClick={() => { setShowNewCustomerForm(false); setPhoneWarning(null); }}
                    className="px-3 text-slate-500 hover:text-slate-700 text-xs border border-slate-200 rounded py-1.5">
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {recentCustomers.length > 0 && !form.customer_id && !customerSearch && (
            <div className="mt-3">
              <p className="text-xs text-slate-400 mb-1.5">Recientes:</p>
              <div className="flex flex-wrap gap-1.5">
                {recentCustomers.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectCustomer(c)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 hover:bg-cyan-100 hover:text-cyan-800 transition-colors border border-slate-200 hover:border-cyan-300"
                  >
                    {c.name} {c.last_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prendas (sub-órdenes) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg">Prendas</CardTitle>
            {subOrders.length > 0 && (
              <p className="text-sm text-slate-500 mt-0.5">
                {subOrders.length} prenda{subOrders.length !== 1 ? 's' : ''} — Total: <span className="font-semibold text-slate-700">{money(totalAmount)}</span>
                {cardSurchargePct > 0 && <span className="text-blue-600"> · {money(Math.round(totalAmount * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</span>}
              </p>
            )}
          </div>
          {!showSubOrderForm && subOrders.length > 0 && (
            <Button type="button" size="sm" onClick={openNewSubOrderForm}
              className="bg-cyan-500 hover:bg-cyan-600 text-white">
              + Agregar prenda
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">

          {/* Sub-orders list */}
          {!showSubOrderForm && subOrders.map((so, idx) => (
            <div key={so.id} className="border border-slate-200 rounded-lg p-3 space-y-1.5 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 flex-shrink-0 bg-cyan-100 text-cyan-800 font-bold text-xs rounded-full w-6 h-6 flex items-center justify-center">
                    {letterOf(idx)}
                  </span>
                  <div>
                    <p className="font-medium text-sm text-slate-900 flex items-center gap-1.5">
                      {so.product_name || '-'}
                      {so.item_type === 'external' && (
                        <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">Externo</span>
                      )}
                    </p>
                    {(so.variant_size || so.variant_color) && (
                      <p className="text-xs text-slate-400">
                        {[so.variant_size, so.variant_color].filter(Boolean).join(' / ')}
                      </p>
                    )}
                    {so.item_description && (
                      <p className="text-xs text-slate-400 italic">{so.item_description}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-0.5">
                      {so.services.map(s => s.name).join('  ·  ')}
                    </p>
                    {so.work_notes && (
                      <p className="text-xs text-slate-400 italic mt-0.5">"{so.work_notes}"</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="font-semibold text-sm text-slate-700">{money(so.subtotal)}</div>
                    {cardSurchargePct > 0 && <div className="text-xs text-blue-600">{money(Math.round(so.subtotal * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</div>}
                  </div>
                  <div className="flex gap-2 text-xs">
                    <button type="button" onClick={() => openEditSubOrderForm(idx)}
                      className="text-slate-400 hover:text-slate-700 font-medium">Editar</button>
                    <button type="button" onClick={() => duplicateSubOrder(idx)}
                      className="text-cyan-400 hover:text-cyan-600 font-medium">Duplicar</button>
                    <button type="button" onClick={() => removeSubOrder(idx)}
                      className="text-rose-400 hover:text-rose-600 font-medium">Quitar</button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add / edit sub-order form */}
          {showSubOrderForm && (
            <div className="border-2 border-cyan-300 rounded-xl p-4 space-y-4 bg-cyan-50">
              <p className="font-semibold text-cyan-900 text-sm">
                {editingIdx !== null
                  ? `Editando prenda ${letterOf(editingIdx)}`
                  : `Prenda ${letterOf(subOrders.length)}`}
              </p>

              {/* Item type toggle */}
              <div className="flex gap-2">
                {['catalog', 'external'].map(t => (
                  <button key={t} type="button"
                    onClick={() => setSoForm({ ...emptySubOrderForm, item_type: t, services: soForm.services, work_notes: soForm.work_notes })}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      soForm.item_type === t
                        ? 'bg-cyan-500 text-white border-cyan-500'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}>
                    {t === 'catalog' ? 'Del catálogo' : 'Prenda externo'}
                  </button>
                ))}
              </div>

              {/* Catalog fields */}
              {soForm.item_type === 'catalog' && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Producto</Label>

                    {/* Top products chips */}
                    {topProducts.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {topProducts.map(p => {
                          const cp = cardPrice(p);
                          const selected = soForm.product_id === p.id;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => { handleSoCatalogProductChange(p.id); setShowProductSearch(false); setProductSearch(''); }}
                              className={`flex flex-col items-start px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                                selected
                                  ? 'bg-cyan-500 text-white border-cyan-500'
                                  : 'bg-white text-slate-700 border-slate-200 hover:border-cyan-400 hover:text-cyan-700'
                              }`}
                            >
                              <span>{p.name}</span>
                              <span className={`text-xs font-normal mt-0.5 ${selected ? 'text-cyan-100' : 'text-slate-400'}`}>
                                {money(p.price)}{cp && <span className={selected ? ' text-cyan-200' : ''}> · {money(cp)} tarjeta</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Search input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={productSearch}
                        onChange={e => { setProductSearch(e.target.value); setShowProductSearch(true); }}
                        onFocus={() => setShowProductSearch(true)}
                        onBlur={() => setTimeout(() => setShowProductSearch(false), 200)}
                        placeholder="Buscar otro producto..."
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                      />
                      {showProductSearch && filteredProducts.length > 0 && (
                        <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                          {filteredProducts.map(p => {
                            const cp = cardPrice(p);
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onMouseDown={() => { handleSoCatalogProductChange(p.id); setProductSearch(''); setShowProductSearch(false); }}
                                className="w-full text-left px-3 py-2 hover:bg-cyan-50 border-b border-slate-100 last:border-0"
                              >
                                <span className="text-sm font-medium text-slate-800">{p.name}</span>
                                <span className="text-xs text-slate-400 ml-2">{money(p.price)} efectivo{cp ? ` · ${money(cp)} tarjeta` : ''}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Selected product display */}
                    {soForm.product_id && (() => {
                      const prod = baseProducts.find(p => p.id === soForm.product_id);
                      const cp = prod ? cardPrice(prod) : null;
                      return prod ? (
                        <>
                          <div className="flex items-center justify-between bg-cyan-500 rounded-lg px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="text-white text-base">✓</span>
                              <span className="font-semibold text-white text-sm">{prod.name}</span>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-white">{money(prod.price)} <span className="text-xs font-normal text-cyan-100">efectivo</span></p>
                              {cp && <p className="text-xs text-cyan-100">{money(cp)} <span className="text-cyan-200">tarjeta</span></p>}
                            </div>
                          </div>
                          {cardSurchargePct > 0 && showPriceNote && (
                            <span className="text-xs text-slate-400">ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> no incluye descuento, al momento de facturar se le agrega el <strong className="text-slate-700">ITBIS</strong>.</span>
                          )}
                        </>
                      ) : null;
                    })()}
                  </div>

                  {soCatalogProduct && soCatalogVariants.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {soAvailableSizes.length > 0 && (
                        <div className="space-y-1">
                          <Label>Talla</Label>
                          <Select value={soForm.variant_size} onValueChange={handleSoSizeChange}>
                            <SelectTrigger className="bg-white"><SelectValue placeholder="Talla" /></SelectTrigger>
                            <SelectContent>
                              {soAvailableSizes.map(s => {
                                const invs     = soCatalogVariants.filter(v => (v.size || '') === s);
                                const hasData  = invs.some(v => v.stock !== null);
                                const stock    = invs.reduce((t, v) => t + (v.stock || 0), 0);
                                return <SelectItem key={s} value={s}>{s}{hasData ? ` (${stock} disp.)` : ''}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {soAvailableColors.length > 0 && (
                        <div className="space-y-1">
                          <Label>Color</Label>
                          <Select value={soForm.variant_color}
                            onValueChange={v => setSoField('variant_color', v)}
                            disabled={soAvailableSizes.length > 0 && !soForm.variant_size}>
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder={soAvailableSizes.length > 0 && !soForm.variant_size ? 'Selecciona talla primero' : 'Color'} />
                            </SelectTrigger>
                            <SelectContent>
                              {soAvailableColors.map(c => {
                                const inv   = soCatalogVariants.find(v => (v.size || '') === (soForm.variant_size || '') && (v.color || '') === c);
                                const stock = inv?.stock ?? null;
                                return <SelectItem key={c} value={c}>{c}{stock !== null ? ` (${stock} disp.)` : ''}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  {soVariantStock !== null && soForm.variant_size && soForm.variant_color && (
                    <p className={`text-xs font-medium ${soVariantStock <= 0 ? 'text-rose-600' : 'text-green-600'}`}>
                      {soVariantStock <= 0
                        ? '⚠ Sin stock para esta variante'
                        : `✓ ${soVariantStock} disponible${soVariantStock !== 1 ? 's' : ''}`}
                    </p>
                  )}
                </div>
              )}

              {/* External fields */}
              {soForm.item_type === 'external' && (
                <div className="space-y-1">
                  <Label>Nombre del prenda</Label>
                  <Input className="bg-white" placeholder="Bata médica, camisa polo..."
                    value={soForm.product_name} onChange={e => setSoField('product_name', e.target.value)} />
                </div>
              )}

              {/* Article description */}
              <div className="space-y-1">
                <Label>Descripción del prenda (opcional)</Label>
                <Input className="bg-white" placeholder="Estado actual, observaciones..."
                  value={soForm.item_description} onChange={e => setSoField('item_description', e.target.value)} />
              </div>

              {/* Services */}
              <div className="space-y-2">
                <Label>Servicios para esta prenda</Label>

                {/* Top services chips */}
                {topServices.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {topServices.map(s => {
                      const cp = cardPrice(s);
                      const already = soForm.services.some(sv => sv.product_id === s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => addSoServiceById(s.id)}
                          disabled={already}
                          className={`flex flex-col items-start px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                            already
                              ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-cyan-400 hover:text-cyan-700'
                          }`}
                        >
                          <span>{s.name}</span>
                          <span className="text-xs font-normal mt-0.5 text-slate-400">
                            {money(s.price)}{cp && <span> · {money(cp)} tarjeta</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Service search */}
                <div className="relative">
                  <input
                    type="text"
                    value={serviceSearch}
                    onChange={e => { setServiceSearch(e.target.value); setShowServiceSearch(true); }}
                    onFocus={() => setShowServiceSearch(true)}
                    onBlur={() => setTimeout(() => setShowServiceSearch(false), 200)}
                    placeholder="Buscar otro servicio..."
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                  />
                  {showServiceSearch && filteredServices.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                      {filteredServices.map(s => {
                        const cp = cardPrice(s);
                        const already = soForm.services.some(sv => sv.product_id === s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onMouseDown={() => { if (!already) addSoServiceById(s.id); setServiceSearch(''); setShowServiceSearch(false); }}
                            className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 ${already ? 'opacity-40 cursor-not-allowed' : 'hover:bg-cyan-50'}`}
                          >
                            <span className="text-sm font-medium text-slate-800">{s.name}</span>
                            <span className="text-xs text-slate-400 ml-2">{money(s.price)} efectivo{cp ? ` · ${money(cp)} tarjeta` : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Selected services */}
                {soForm.services.length > 0 && (
                  <div className="space-y-1">
                    {soForm.services.map(s => {
                      const cp = cardSurchargePct > 0 ? Math.round(s.unit_price * (1 + cardSurchargePct / 100) * 100) / 100 : null;
                      return (
                        <div key={s.product_id} className="flex items-center justify-between bg-cyan-500 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-base">✓</span>
                            <span className="font-semibold text-white text-sm">
                              {s.name}{s.quantity > 1 && <span className="ml-1 text-cyan-100 font-normal">×{s.quantity}</span>}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm font-medium text-white">{money(s.subtotal)} <span className="text-xs font-normal text-cyan-100">efectivo</span></p>
                              {cp && <p className="text-xs text-cyan-100">{money(cp * s.quantity)} <span className="text-cyan-200">tarjeta</span></p>}
                            </div>
                            <button type="button" onClick={() => removeServiceFromSubOrder(s.product_id)}
                              className="text-cyan-200 hover:text-white text-sm leading-none">✕</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label>Notas <span className="text-rose-500 text-xs">*</span></Label>
                <Textarea className="bg-white" rows={2}
                  placeholder="Texto a bordar, posición, color del hilo, medidas específicas..."
                  value={soForm.work_notes} onChange={e => setSoField('work_notes', e.target.value)} />
              </div>

              {/* Subtotal preview */}
              {soSubtotal > 0 && (
                <div className="text-right text-sm text-slate-600 space-y-0.5">
                  <div>
                    {soItemPrice > 0 && <span className="mr-4">Prenda: {money(soItemPrice)}{cardSurchargePct > 0 && <span className="text-blue-500 ml-1">· {money(Math.round(soItemPrice * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</span>}</span>}
                    {soServicesTotal > 0 && <span className="mr-4">Servicios: {money(soServicesTotal)}{cardSurchargePct > 0 && <span className="text-blue-500 ml-1">· {money(Math.round(soServicesTotal * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</span>}</span>}
                  </div>
                  <div>
                    <span className="font-bold text-slate-900">Subtotal: {money(soSubtotal)}</span>
                    {cardSurchargePct > 0 && <span className="font-bold text-blue-600 ml-2">· {money(Math.round(soSubtotal * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</span>}
                  </div>
                </div>
              )}

              {/* Form actions */}
              <div className="flex justify-center gap-2">
                <Button type="button" onClick={saveSubOrder}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white w-40 h-11">
                  {editingIdx !== null ? 'Guardar cambios' : 'Agregar prenda'}
                </Button>
                <Button type="button" variant="outline" className="w-40 h-11" onClick={cancelSubOrderForm}>Cancelar</Button>
              </div>
            </div>
          )}

          {/* Empty state + first add button */}
          {!showSubOrderForm && subOrders.length === 0 && (
            <button type="button" onClick={openNewSubOrderForm}
              className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 text-slate-400 hover:border-cyan-400 hover:text-cyan-600 transition-colors text-sm font-medium">
              + Agregar primera prenda
            </button>
          )}

          {/* "Add another" when there are items but form is closed */}
          {!showSubOrderForm && subOrders.length > 0 && (
            <button type="button" onClick={openNewSubOrderForm}
              className="w-full border border-dashed border-slate-200 rounded-lg py-3 text-slate-400 hover:border-cyan-400 hover:text-cyan-500 transition-colors text-sm">
              + Agregar otra prenda
            </button>
          )}

        </CardContent>
      </Card>

      {/* Detalles generales del trabajo */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Notas generales</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            placeholder="Urgente para el viernes, llamar antes de entregar..."
            value={form.work_notes}
            onChange={e => setField('work_notes', e.target.value)}
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Fecha de entrega */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Fecha de entrega</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          <Input type="date" value={form.due_date} onChange={e => setField('due_date', e.target.value)} />
          <p className="text-xs text-slate-500">Por defecto: 7 días a partir de hoy.</p>
        </CardContent>
      </Card>

      {/* Pago inicial */}
      {totalAmount > 0 && (() => {
        const isCardPay = paymentMethod === 'tarjeta' && cardSurchargePct > 0;
        const effTotal  = isCardPay ? totalWithSurcharge : totalAmount;
        const anticipo  = parseFloat(form.initial_payment) || 0;
        const balance   = effTotal - anticipo;
        const hasInput  = form.initial_payment !== '';
        const isValid   = anticipo >= 0 && anticipo <= effTotal;
        return (
          <Card className={`border-2 ${hasInput && isValid ? 'border-green-300 bg-green-50' : hasInput ? 'border-rose-300 bg-rose-50' : 'border-slate-200'}`}>
            <CardHeader className="pb-3"><CardTitle className="text-lg">Pago inicial</CardTitle></CardHeader>
            <CardContent className="space-y-4">

              {/* Método de pago */}
              {cardSurchargePct > 0 && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-700">Método de pago</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'efectivo', label: '💵 Efectivo' },
                      { value: 'tarjeta',  label: '💳 Tarjeta' },
                      { value: 'transferencia', label: '🏦 Transferencia' },
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => { setPaymentMethod(opt.value); setField('initial_payment', ''); }}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          paymentMethod === opt.value
                            ? 'bg-cyan-500 text-white border-cyan-500'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400 hover:text-cyan-600'
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="bg-white rounded-xl p-4 border border-cyan-100 text-center">
                <p className="text-xs text-slate-500 mb-1">Total de la orden ({subOrders.length} prenda{subOrders.length !== 1 ? 's' : ''})</p>
                {paymentMethod === 'tarjeta' && cardSurchargePct > 0 ? (
                  <div>
                    <p className="text-2xl font-bold text-cyan-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(totalWithSurcharge)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {money(totalAmount)} + {cardSurchargePct}% recargo tarjeta ({money(cardSurchargeAmt)})
                    </p>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-cyan-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(totalAmount)}</p>
                )}
              </div>

              {/* Anticipo */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-2">
                <label className="text-sm font-medium text-slate-700">Anticipo a cobrar ahora</label>
                <div className="flex gap-2">
                  {[
                    { label: '15%',  value: effTotal * 0.15 },
                    { label: '25%',  value: effTotal * 0.25 },
                    { label: '50%',  value: effTotal * 0.5 },
                    { label: '75%',  value: effTotal * 0.75 },
                    { label: '100%', value: effTotal },
                  ].map(opt => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setField('initial_payment', opt.value.toFixed(2))}
                      className={`flex-1 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                        parseFloat(form.initial_payment) === parseFloat(opt.value.toFixed(2))
                          ? 'bg-cyan-500 text-white border-cyan-500'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400 hover:text-cyan-600'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
                <input
                  type="number" min="0" step="0.01" max={effTotal}
                  value={form.initial_payment}
                  onChange={e => setField('initial_payment', e.target.value)}
                  placeholder={`Máximo ${money(effTotal)}`}
                  className={`w-full border-2 rounded-lg px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 ${
                    hasInput && !isValid
                      ? 'border-rose-400 focus:ring-rose-400 bg-rose-50'
                      : hasInput && isValid
                      ? 'border-green-400 focus:ring-green-400 bg-green-50'
                      : 'border-slate-200 focus:ring-cyan-400'
                  }`}
                />
                {hasInput && !isValid && (
                  <p className="text-xs text-rose-600">El anticipo no puede superar el total ({money(effTotal)})</p>
                )}
                {hasInput && isValid && anticipo > 0 && (
                  <div className="space-y-0.5">
                    <p className="text-xs text-green-600">Balance restante: {money(Math.max(0, balance))}</p>
                    {isCardPay && (
                      <p className="text-xs text-slate-400">Equivale a {money(Math.round(anticipo / CARD_FACTOR * 100) / 100)} en efectivo — el recargo se aplica al cobrar en el POS</p>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Deja en 0 si no se cobra anticipo ahora. Si ingresas un monto, irás al POS para cobrarlo.
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Actions */}
      <div className="flex gap-2 pb-6">
        <Button onClick={submit} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-white">
          {saving ? 'Guardando...' : (parseFloat(form.initial_payment) > 0 ? 'Guardar y cobrar' : 'Guardar orden')}
        </Button>
        <Button variant="outline" onClick={() => navigate('/service-orders')} disabled={saving}>
          Cancelar
        </Button>
      </div>

      <style>{`
        .phone-input-container .PhoneInput {
          display: flex; align-items: center; height: 34px;
          border: 1px solid #e2e8f0; border-radius: 6px;
          background: white; padding: 0 8px; gap: 6px; font-size: 14px;
        }
        .phone-input-container .PhoneInput:focus-within {
          border-color: #06b6d4; box-shadow: 0 0 0 2px rgba(6,182,212,0.25);
        }
        .phone-input-container .PhoneInputInput {
          flex: 1; border: none; outline: none; background: transparent; font-size: 13px; color: #0f172a;
        }
        .phone-input-container .PhoneInputCountrySelect { border: none; background: transparent; cursor: pointer; }
        .phone-input-container .PhoneInputCountryIcon   { width: 18px; height: 13px; }
      `}</style>
    </div>
  );
};

export default ServiceOrderNew;
